import React from 'react'
import SearchDropdown from '@SiteComponents/SearchDropdown'
import classnames from 'classnames'
import Warning from '@Images/import_terms_warning.svg'

interface ColumnMappingProps {
  readonly dataHeader: string[]
  readonly termbaseLanguages: string[]
  readonly sourceLanguage: string
  readonly targetLanguage: string
  readonly sourceTermsColumnHeader: string
  readonly translationTermsColumnHeader: string
  readonly sourcePronunciationColumnHeader: string
  readonly targetPronunciationColumnHeader: string
  readonly noSelectionOption: string
  readonly warningSourceTerms: string | null
  readonly warningSourceLanguage: string | null
  readonly warningTranslationTerms: string | null
  readonly warningTargetLanguage: string | null
  setSourceLanguage(newSourceLanguage: string): void
  setTargetLanguage(newTargetlanguage: string): void
  setSourceTermsColumnHeader(newSourceTermsColumnHeader: string): void
  setTranslationTermsColumnHeader(newTranslationTermsColumnHeader: string): void
  setSourcePronunciationColumnHeader(newSourcePronunciationColumnHeader: string): void
  setTargetPronunciationColumnHeader(newTargetPronunciationColumnHeader: string): void
}

const ColumnMapping: React.FC<ColumnMappingProps>= props => {
  const {
    dataHeader,
    termbaseLanguages,
    sourceLanguage,
    targetLanguage,
    sourceTermsColumnHeader,
    translationTermsColumnHeader,
    sourcePronunciationColumnHeader,
    targetPronunciationColumnHeader,
    noSelectionOption,
    warningSourceTerms,
    warningSourceLanguage,
    warningTranslationTerms,
    warningTargetLanguage,
  } = props

  const dataHeaderWithNoSelectionOption = [noSelectionOption].concat(dataHeader)
  const termbaseLanguagesWithNoSelectionOption = [noSelectionOption].concat(termbaseLanguages)
  const hasWarningForSourceTerms = !!warningSourceTerms
  const hasWarningForSourceLanguage = !!warningSourceLanguage
  const hasWarningForTranslationTerms = !!warningTranslationTerms
  const hasWarningForTargetLanguage = !!warningTargetLanguage

  const renderWarning = (warning: string) => {
    return (
      <div className="ImportTermsPopup__warning">
        <Warning />
        <div>
          {warning}
        </div>
      </div>
    )
  }

  const renderSourceTerm = () => {
    return (
      <div className="ImportTermsPopup__column-mapping-item ImportTermsPopup__column-mapping-item-terms">
        <div className="ImportTermsPopup__column-mapping-item-header">
          Source Term
        </div>
        <div
          className="ImportTermsPopup__source-term-wrapper"
          id="import-terms-popup-source-term-wrapper"
        >
          <SearchDropdown
            availableOptions={dataHeader}
            selectedOption={sourceTermsColumnHeader}
            classNameForClickOutsideListener="ImportTermsPopup__source-term"
            idNameOfParentContainer="import-terms-popup-source-term-wrapper"
            placeholderText="Select matching column..."
            setSelectedOption={props.setSourceTermsColumnHeader}
            showAlert={hasWarningForSourceTerms}
          />
        </div>
        {hasWarningForSourceTerms && renderWarning(warningSourceTerms)}
      </div>
    )
  }

  const renderSourceLanguage = () => {
    return (
      <div
        className="ImportTermsPopup__column-mapping-item ImportTermsPopup__column-mapping-item-language">
        <div className="ImportTermsPopup__column-mapping-item-header">
          Source Language
        </div>
        <div
          className="ImportTermsPopup__source-language-wrapper"
          id="import-terms-popup-source-language-wrapper"
        >
          <SearchDropdown
            availableOptions={termbaseLanguages}
            selectedOption={sourceLanguage}
            classNameForClickOutsideListener="ImportTermsPopup__source-language"
            idNameOfParentContainer="import-terms-popup-source-language-wrapper"
            placeholderText="Select language..."
            setSelectedOption={props.setSourceLanguage}
            showAlert={hasWarningForSourceLanguage}
          />
        </div>
        {hasWarningForSourceLanguage && renderWarning(warningSourceLanguage)}
      </div>
    )
  }

  const renderSourcePronunciation = () => {
    const noSelection = sourcePronunciationColumnHeader === noSelectionOption

    return (
      <div
        className="ImportTermsPopup__column-mapping-item ImportTermsPopup__column-mapping-item-terms">
        <div className="ImportTermsPopup__column-mapping-item-header">
          Source Pronunciation
        </div>
        <div
          className="ImportTermsPopup__source-pronunciation-wrapper"
          id="import-terms-popup-source-pronunciation-wrapper"
        >
          <SearchDropdown
            availableOptions={noSelection ? dataHeader: dataHeaderWithNoSelectionOption}
            selectedOption={sourcePronunciationColumnHeader}
            classNameForClickOutsideListener="ImportTermsPopup__source-pronunciation"
            idNameOfParentContainer="import-terms-popup-source-pronunciation-wrapper"
            placeholderText="Select matching column..."
            setSelectedOption={props.setSourcePronunciationColumnHeader}
          />
        </div>
      </div>
    )
  }

  const renderTranslationTerm = () => {
    const noSelection = translationTermsColumnHeader === noSelectionOption

    return (
      <div
        className="ImportTermsPopup__column-mapping-item ImportTermsPopup__column-mapping-item-terms">
        <div className="ImportTermsPopup__column-mapping-item-header">
          Translation Term
        </div>
        <div
          className="ImportTermsPopup__translation-term-wrapper"
          id="import-terms-popup-translation-term-wrapper"
        >
          <SearchDropdown
            availableOptions={noSelection ? dataHeader : dataHeaderWithNoSelectionOption}
            selectedOption={translationTermsColumnHeader}
            classNameForClickOutsideListener="ImportTermsPopup__translation-term"
            idNameOfParentContainer="import-terms-popup-translation-term-wrapper"
            placeholderText="Select matching column..."
            setSelectedOption={props.setTranslationTermsColumnHeader}
            showAlert={hasWarningForTranslationTerms}
          />
        </div>
        {hasWarningForTranslationTerms && renderWarning(warningTranslationTerms)}
      </div>
    )
  }

  const renderTargetLanguage = () => {
    const noSelection = targetLanguage === noSelectionOption

    return (
      <div
        className="ImportTermsPopup__column-mapping-item ImportTermsPopup__column-mapping-item-language">
        <div className="ImportTermsPopup__column-mapping-item-header">
          Target Language
        </div>
        <div
          className="ImportTermsPopup__target-language-wrapper"
          id="import-terms-popup-target-language-wrapper"
        >
          <SearchDropdown
            availableOptions={noSelection ? termbaseLanguages: termbaseLanguagesWithNoSelectionOption}
            selectedOption={targetLanguage}
            classNameForClickOutsideListener="ImportTermsPopup__target-language"
            idNameOfParentContainer="import-terms-popup-target-language-wrapper"
            placeholderText="Select language..."
            setSelectedOption={props.setTargetLanguage}
            showAlert={hasWarningForTargetLanguage}
          />
        </div>
        {hasWarningForTargetLanguage && renderWarning(warningTargetLanguage)}
      </div>
    )
  }

  const renderTargetPronunciation = () => {
    const noSelection = targetPronunciationColumnHeader === noSelectionOption

    return (
      <div
        className="ImportTermsPopup__column-mapping-item ImportTermsPopup__column-mapping-item-terms">
        <div className="ImportTermsPopup__column-mapping-item-header">
          Target Pronunciation
        </div>
        <div
          className="ImportTermsPopup__target-pronunciation-wrapper"
          id="import-terms-popup-target-pronunciation-wrapper"
        >
          <SearchDropdown
            availableOptions={noSelection ? dataHeader : dataHeaderWithNoSelectionOption}
            selectedOption={targetPronunciationColumnHeader}
            classNameForClickOutsideListener="ImportTermsPopup__target-pronunciation"
            idNameOfParentContainer="import-terms-popup-target-pronunciation-wrapper"
            placeholderText="Select matching column..."
            setSelectedOption={props.setTargetPronunciationColumnHeader}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="ImportTermsPopup__column-mapping">
      <div
        className={classnames(
          'ImportTermsPopup__column-mapping-source',
          { 'ImportTermsPopup__column-mapping-source-alert': hasWarningForSourceTerms || hasWarningForSourceLanguage }
        )}
      >
        {renderSourceTerm()}
        {renderSourceLanguage()}
        {renderSourcePronunciation()}
      </div>
      <div
        className={classnames(
          'ImportTermsPopup__column-mapping-target',
          { 'ImportTermsPopup__column-mapping-target-alert': hasWarningForTranslationTerms || hasWarningForTargetLanguage }
        )}
      >
        {renderTranslationTerm()}
        {renderTargetLanguage()}
        {renderTargetPronunciation()}
      </div>
    </div>
  )
}

export default ColumnMapping
